/**
 * Product form functionality for add-to-cart operations
 * Handles form submission, variant selection, tiered pricing, quantity management,
 * and integration with cart drawer/popup based on window.cartMode
 *
 * Supports A/B testing via window.cartMode:
 * - 'drawer': Opens cart drawer after add to cart (default)
 * - 'page': Shows popup notification after add to cart
 */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      // Cart drawer (for drawer mode)
      this.cartDrawer = document.querySelector('cart-drawer');

      // Cart notification/popup (for page mode)
      this.cartNotification = document.querySelector('cart-notification');
      if (!this.cartNotification) {
        this.cartNotification = document.querySelector('cart-add-popup');
        if (this.cartNotification) {
          this.productRecommendationsInline = this.cartNotification.querySelector('product-recommendations-inline');
        }
      }

      // Cart page elements (when on /cart)
      let url = window.location.href;
      if (url && url.includes('cart')) {
        this.cartItems = document.querySelector('cart-items');
        this.cartPoints = document.querySelector('cart-points');
      } else {
        this.cartPoints = null;
        this.cartItems = null;
      }

      // Form
      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      // Variants
      this.variants = this.querySelector('.product__variants select');
      this.variants?.addEventListener('change', () => this.handleVariantSelect());

      // Tiered pricing price
      this.tiers = window.tieredPricing.tiers;

      // Quantity selector
      this.quantityInputElements = this.querySelectorAll('quantity-input[data-quantity-input-behavior="single"]');
      this.quantityInputElements.forEach(element => {
        this.quantityInput = element.querySelector('input');
        this.preventDiscount = (element.dataset.preventDiscount === 'true');

        ['change', 'input'].forEach((inputEvent) => {
          this.quantityInput.addEventListener(inputEvent, ({ target }) => this.handlePriceAndBadge(target?.valueAsNumber ?? 1));
        });
      });


      this.getElements();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.handleAllPricesAndBadges());
      } else {
        this.handleAllPricesAndBadges();
      }
    }


    getElements() {

      this.tieredPricingPriceElements = this.querySelectorAll('tiered-pricing-price');

      this.tieredPricingBadgeElements = this.querySelectorAll('tiered-pricing-badge');

      this.tieredUnitPricingElements = this.querySelectorAll('tiered-pricing-unit-price');

      this.tieredPricingProgressContainer = this.querySelectorAll('tiered-pricing-progress-container');


    }

    /**
     * Handle submit
     * @param {Object} e
     * @returns void
     */
    async onSubmitHandler(e) {
      e.preventDefault();

      const submitButtons = this.querySelectorAll('[type="submit"]');

      submitButtons.forEach(element => {
        element.setAttribute('disabled', true);
        element.classList.add('loading');
      });

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      // Remove bundled products if applicable (for page mode bundles)
      const skus_to_remove = formData.get('properties[_bundeled_products_skus]');
      if (skus_to_remove) {
        const skusArray = skus_to_remove.split(',').map(sku => sku.trim());
        try {
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          const cartData = await cartResponse.json();

          const skuQuantities = skusArray.reduce((acc, sku) => {
            acc[sku] = (acc[sku] || 0) + 1;
            return acc;
          }, {});

          for (const item of cartData.items) {
            const variantSku = item.sku;
            const quantityToRemove = skuQuantities[variantSku];

            if (quantityToRemove) {
              const newQuantity = item.quantity - quantityToRemove;
              await this.updateQuantity(item.variant_id, Math.max(newQuantity, 0));
            }
          }
        } catch (error) {
          console.error('Error fetching cart data:', error);
        }
      }

      // Check cart mode at runtime (allows VWO to override)
      const isDrawerMode = window.cartMode === 'drawer';
      let upsell = false;
      let sectionsToFetch = [];

      if (isDrawerMode && this.cartDrawer) {
        // Drawer mode: get sections for cart drawer
        sectionsToFetch = [
          ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
        ];
      } else if (this.cartNotification) {
        // Page mode: get sections for popup notification
        sectionsToFetch = [
          ...this.cartNotification.getSectionsToRender().map((section) => section.id)
        ];

        if (this.classList.contains('upsell')) {
          sectionsToFetch = [
            ...this.cartNotification.getSectionsToRenderAfterUpsell().map((section) => section.id)
          ];
          upsell = true;
        }
      } else if (this.cartItems) {
        // On cart page
        sectionsToFetch = [
          ...this.cartItems.getSectionsToRender().map((section) => section.id)
        ];

        if (this.classList.contains('upsell')) {
          sectionsToFetch = [
            ...this.cartItems.getSectionsToRenderOnChange().map((section) => section.section)
          ];
          upsell = true;
        }
      }

      formData.append('sections', sectionsToFetch);
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const json = await response.json();

        // Quantity error handling
        if (response.status == 422) {
          if (this.cartNotification) {
            this.cartNotification.setActiveElement(document.activeElement);
            this.cartNotification.renderQuantityError(formData.get('inventory_quantity'));
          }
          return;
        }

        // Update the window value for tiered pricing
        let valueAsNumber = !isNaN(this.quantityInput?.valueAsNumber) ? this.quantityInput.valueAsNumber : 1;
        window.tieredPricing.tiered_pricing_item_count = window.tieredPricing.tiered_pricing_item_count + (valueAsNumber * json.properties._tiered_pricing_count);

        // A/B test: Check window.cartMode at runtime to decide behavior
        if (isDrawerMode && this.cartDrawer) {
          // DRAWER MODE: Open the cart drawer
          this.cartDrawer.renderContents(json);
          this.cartDrawer.open();
        } else if (this.cartNotification) {
          // PAGE MODE: Show the popup notification
          this.cartNotification.renderContents(json, upsell);

          // Handle product recommendations
          if (!this.productRecommendationsInline) {
            this.productRecommendationsInline = document.querySelector('product-recommendations-inline');
          }

          if (!upsell && this.productRecommendationsInline) {
            let productId = this.querySelector('[name="product-id"]').value;
            this.productRecommendationsInline.dataset.productId = productId;
            this.productRecommendationsInline.dataset.url = window.Shopify.routes.root + `recommendations/products?section_id=product-recommendations-inline&product_id=` + productId + `&limit=3&intent=complementary`;
            this.productRecommendationsInline.getRecommendations();
          }
        } else if (this.cartItems) {
          // On cart page
          this.cartItems.getSectionsToRenderOnChange().forEach((section) => {
            const { id, selector, section: newSection } = section;
            const elementToReplace =
              document.getElementById(id).querySelector(selector) ||
              document.getElementById(id);

            elementToReplace.innerHTML = this.cartItems.getSectionInnerHTML(
              json.sections[newSection],
              selector
            );
          });
          this.cartItems.calculateShippingFee();
        }

        this.handleAllPricesAndBadges();
        this.resetQuantityInputs();

      } catch (error) {
        console.error(error);
      } finally {
        submitButtons.forEach(element => {
          element.classList.remove('loading');
          if (upsell) {
            element.classList.add('added');
          }
          element.removeAttribute('disabled');
        });
      }
    }

    /**
     * Updates cart item quantity for bundle product removal
     * @param {number} id - Variant ID to update
     * @param {number} quantity - New quantity value
     */
    async updateQuantity(id, quantity) {
      const body = JSON.stringify({
        updates: {
          [id]: quantity,
        },
      });

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = body;

      try {
        await fetch(`${routes.cart_update_url}`, config);
      } catch (error) {
        console.error(error);
      }
    }


    /**
     * Resets all quantity inputs to default value of 1
     */
    resetQuantityInputs() {
      this.quantityInputElements.forEach(element => {
        this.quantityInput = element.querySelector('input');
        this.quantityInput.defaultValue = 1;
        this.quantityInput.value = 1;
      });

    }


    /**
     * Handle price and badge
     * @param {Number} valueAsNumber
     */
    handlePriceAndBadge(valueAsNumber) {
      // Updated to match refactored tieredPricingPrice which handles tier determination internally
      // No longer loops through tiers - each element determines its own applicable tier

      this.tieredPricingPriceElements.forEach(element => {
        if (element.handlePrice) {
          element.handlePrice(valueAsNumber);
        }
      });

      // Badges and unit prices still use the old pattern with tier loops
      this.tiers.forEach(({ tiered_pricing_threshold, tiered_pricing_discount }) => {
        this.tieredPricingBadgeElements.forEach(element => {
          if (element.handleBadge) {
            element.handleBadge(valueAsNumber, tiered_pricing_threshold, tiered_pricing_discount);
          }
        });
        this.tieredUnitPricingElements.forEach(element => {
          if (element.handleUnitPrice) {
            element.handleUnitPrice(valueAsNumber, tiered_pricing_threshold, tiered_pricing_discount);
          }
        });
      });

      this.handleQuantityDiscountSnippet(valueAsNumber);
    }

    /**
     * Handle all prices and badges
     */
    handleAllPricesAndBadges() {
      const tiers = window.tieredPricing.tiers;

      const pricesElements = document.querySelectorAll('tiered-pricing-price');
      const badgesElements = document.querySelectorAll('tiered-pricing-badge');

      // Updated to match refactored tieredPricingPrice which handles tier determination internally
      pricesElements.forEach(priceElement => {
        if (priceElement.handlePrice) {
          priceElement.handlePrice(false);
        }
      });

      // Badges still use the old pattern with tier loops
      tiers.forEach(({ tiered_pricing_threshold, tiered_pricing_discount }) => {
        badgesElements.forEach(badgeElement => {
          if (badgeElement.handleBadge) {
            badgeElement.handleBadge(false, tiered_pricing_threshold, tiered_pricing_discount);
          }
        });
      });

      this.handleQuantityDiscountSnippet();
    }

    /**
     * Updates quantity discount progress bar visual indicator
     * @param {number} valueAsNumber - Current quantity value
     */
    handleQuantityDiscountSnippet(valueAsNumber = 1) {
      let progress = 0;
      let oneProductDiscount = 0;
      if (this.tiers[0].tiered_pricing_threshold == 1 && this.tiers[0].tiered_pricing_discount > 0) {
        oneProductDiscount = 1;
      }
      let tieredPricingItemCount = window.tieredPricing.tiered_pricing_item_count ?? 0;
      progress = ((valueAsNumber * tieredPricingItemCount) / (this.tiers.length + 1 - (oneProductDiscount))) * 100;
      const progressBar = this.querySelector('.progress-bar');
      if (progress >= 100) { progress = 100; }
      if (progressBar) {
        progressBar.style.width = progress + '%';
      }
    }

    /**
     * Handle variant change
     */
    handleVariantSelect() {
      this.variants.options[this.variants.selectedIndex].value && (window.location = this.variants.options[this.variants.selectedIndex].value)
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}


